import { apiClient } from './api/apiClient';
import {
  ActiveBatchResponse,
  BatchStep,
  BatchViewResponse,
  RetryStepRequest,
  RetryStepResponse,
  StartBatchRequest,
  StartBatchResponse,
  SiteBatch,
  RunningSite,
  SITE_BATCH_SERVICE_CATALOG,
  StepStatus,
} from '../models/SiteBatch';

/**
 * Backend 의 DeploymentResult 행은 `step_index`, `id` 등의 GORM 컬럼 네이밍으로
 * 내려온다. Frontend BatchStep 은 `index`, `deployment_id`, `label` 을 쓰므로
 * 서비스 경계에서 한 번 정규화한다.
 *
 * 이 함수가 없으면 mergeStepsWithCatalog 가 `s.index === undefined` 인 raw 를
 * 못 찾고 카탈로그의 waiting 플레이스홀더만 렌더해서 UI 가 "8개 모두 대기" 로 보이는
 * 현상이 재현된다 (실제 batch 의 success_count 는 올바른데 개별 행만 빈 상태).
 */
function normalizeStep(raw: any): BatchStep {
  const index: number = raw.step_index ?? raw.index ?? 0;
  // deploy-service-selection 이후 step.step_index 는 progress position (1..N) 이며
  // 선택된 subset 에 따라 config 위치와 일치하지 않는다. label 은 catalog 의 appname
  // 으로 조회한다.
  const appname = raw.appname ?? '';
  const catalog = SITE_BATCH_SERVICE_CATALOG.find((c) => c.appname === appname);
  return {
    index,
    appname,
    label: catalog?.label ?? appname ?? `Step ${index}`,
    status: (raw.status ?? 'waiting') as StepStatus,
    deployment_id: raw.id ?? raw.deployment_id,
    dispatched_at: raw.dispatched_at,
    // Backend 가 아직 step 단위 finished_at 을 별도 컬럼으로 관리하지 않아
    // 보수적으로 updated_at 을 proxy 로 사용. duration 계산 근사치이지만
    // 요약 카운터(batch.success_count) 는 이 값과 무관하므로 UX 에 큰 영향 없음.
    finished_at: raw.finished_at ?? raw.updated_at,
    duration_sec:
      raw.duration_sec ??
      (raw.dispatched_at && (raw.finished_at ?? raw.updated_at)
        ? Math.max(
            0,
            Math.round(
              (new Date(raw.finished_at ?? raw.updated_at).getTime() -
                new Date(raw.dispatched_at).getTime()) /
                1000,
            ),
          )
        : undefined),
    failure_reason: raw.failure_reason,
    // deploy-failure-github-link: backend DeploymentResult.github_run_id 노출
    github_run_id: raw.github_run_id,
  };
}

/**
 * Client for the site-batch-deploy REST API.
 *
 * Backend routes (features/parking/batch/handler/batchHandler.go):
 *   POST   /v0.1/parking/:projectId/deployments/site-batch
 *   GET    /v0.1/parking/:projectId/deployments/site-batch/active?site=<site>
 *   GET    /v0.1/parking/:projectId/deployments/site-batch/:batchId
 *   POST   /v0.1/parking/:projectId/deployments/site-batch/:batchId/retry-step
 *
 * All endpoints require JWT (via apiClient interceptor). The backend
 * enforces a single active batch per site — attempting to start while
 * one is running returns HTTP 409 which we surface as a typed error.
 */
class SiteBatchService {
  /** Normalize the backend envelope into a plain SiteBatch with steps. */
  private unwrap(res: BatchViewResponse): SiteBatch {
    const { batch, steps } = res.data;
    const normalized = (steps ?? []).map((s) => normalizeStep(s));
    return { ...batch, steps: normalized };
  }

  async startBatch(projectId: string, body: StartBatchRequest): Promise<SiteBatch> {
    const res = await apiClient.post<StartBatchResponse>(
      `/v0.1/parking/${projectId}/deployments/site-batch`,
      body,
    );
    return this.unwrap(res.data);
  }

  async getBatch(projectId: string, batchId: number): Promise<SiteBatch> {
    const res = await apiClient.get<BatchViewResponse>(
      `/v0.1/parking/${projectId}/deployments/site-batch/${batchId}`,
    );
    return this.unwrap(res.data);
  }

  async retryStep(projectId: string, batchId: number, body: RetryStepRequest): Promise<RetryStepResponse['data']> {
    const res = await apiClient.post<RetryStepResponse>(
      `/v0.1/parking/${projectId}/deployments/site-batch/${batchId}/retry-step`,
      body,
    );
    return res.data.data;
  }

  /**
   * List every running/pending batch across all sites. Used by the site
   * management table to decorate rows with "배포 중 (N/8)" indicators.
   *
   * The returned entries DO NOT include the steps[] array — call getBatch()
   * for full progress once a user opens the modal.
   */
  async listActiveBatches(projectId: string): Promise<SiteBatch[]> {
    const res = await apiClient.get<{ success: boolean; data: SiteBatch[] | null }>(
      `/v0.1/parking/${projectId}/deployments/site-batch/active-all`,
    );
    return (res.data.data ?? []).map((b) => ({ ...b, steps: b.steps ?? [] }));
  }

  /**
   * List sites currently "구동 중" — those whose most recent completed
   * batch had a successful router (step 1) deployment. Used by the
   * "현재 구동 중인 현장" banner and row badges.
   */
  async listRunningSites(projectId: string): Promise<RunningSite[]> {
    const res = await apiClient.get<{ success: boolean; data: RunningSite[] | null }>(
      `/v0.1/parking/${projectId}/deployments/site-batch/running-sites`,
    );
    return res.data.data ?? [];
  }

  async findActiveBatch(projectId: string, site: string): Promise<SiteBatch | null> {
    const res = await apiClient.get<ActiveBatchResponse>(
      `/v0.1/parking/${projectId}/deployments/site-batch/active`,
      { params: { site } },
    );
    // Active endpoint returns only the batch row (no steps). Callers who
    // need full progress must follow up with getBatch().
    if (!res.data.data) return null;
    return { ...res.data.data, steps: res.data.data.steps ?? [] };
  }

  /**
   * Force-cancel a batch. Used as the recovery path when a 409
   * "active_batch_exists" is returned from startBatch — usually because
   * a previous run's webhook never arrived.
   */
  async cancelBatch(projectId: string, batchId: number, reason?: string): Promise<void> {
    await apiClient.post(
      `/v0.1/parking/${projectId}/deployments/site-batch/${batchId}/cancel`,
      { reason: reason ?? '' },
    );
  }

  /**
   * Cancel whatever batch is currently active for a site, without needing
   * to know the batch id. Safe when no active batch exists (returns data=null).
   */
  async cancelActiveBatchBySite(projectId: string, site: string, reason?: string): Promise<void> {
    await apiClient.post(
      `/v0.1/parking/${projectId}/deployments/site-batch/active/cancel`,
      { site, reason: reason ?? '' },
    );
  }

  /**
   * Stop every service at a site. Triggers company-deploy.yaml with
   * action=stop + appname=config-only, which runs `docker compose down`
   * on the remote server. Any in-flight batch is cancelled first by
   * the backend so the UI state stays consistent.
   */
  async stopSite(projectId: string, site: string): Promise<void> {
    await apiClient.post(
      `/v0.1/parking/${projectId}/deployments/site-batch/stop`,
      { site },
    );
  }

  /**
   * Wait until the active batch on `site` disappears — i.e. the stop
   * batch (config-only/action=stop) has finished tearing down the
   * docker stack. Used by local-multisite-auto-swap so the new site's
   * `docker compose up` does not race with the previous site's
   * `docker compose down` and collide on shared container names/ports.
   *
   * Polls findActiveBatch every `intervalMs`; resolves the moment the
   * endpoint returns null (terminal completed/cancelled batches drop
   * out of the active query). Throws Error('stop_timeout') if the
   * deadline elapses while a pending/running batch is still in flight.
   *
   * status='cancelled' is treated as completion: the operator's "취소"
   * mid-flight and the backend's auto-cancel-on-stop both release the
   * docker resources, so the swap should proceed.
   */
  async waitForStopCompletion(
    projectId: string,
    site: string,
    timeoutMs: number = 60_000,
    intervalMs: number = 1_500,
  ): Promise<void> {
    const deadline = Date.now() + timeoutMs;

    // First check is immediate so a no-op stop returns without waiting.
    while (true) {
      const active = await this.findActiveBatch(projectId, site);

      if (active === null) return;
      if (active.status === 'cancelled') return;

      if (Date.now() >= deadline) throw new Error('stop_timeout');

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  /**
   * Fetch the GitHub Actions log for a specific batch step.
   * Returns the raw log text (or empty string when not yet available).
   * Used by the inline "로그 보기" panel in the batch progress modal.
   */
  async getStepLogs(projectId: string, batchId: number, stepIndex: number): Promise<string> {
    const res = await apiClient.get<{ success: boolean; data: { logs: string } }>(
      `/v0.1/parking/${projectId}/deployments/site-batch/${batchId}/steps/${stepIndex}/logs`,
    );
    return res.data.data.logs ?? '';
  }

  /**
   * List available image versions for a service. Used by the deploy
   * dialog's "reuse" mode dropdown (deploy-service-mode-select feature).
   * The backend proxies GitHub Actions tags under `{appname}-v*` prefix;
   * `limit` caps the result at `min(limit, 100)` entries, newest-first.
   */
  async listServiceVersions(
    projectId: string,
    appname: string,
    limit = 20,
  ): Promise<Array<{ name: string; sha?: string }>> {
    const res = await apiClient.get<{
      success: boolean;
      data: { appname: string; versions: Array<{ name: string; sha?: string }> };
    }>(
      `/v0.1/parking/${projectId}/deployments/services/${appname}/versions`,
      { params: { limit } },
    );
    return res.data.data.versions ?? [];
  }
}

const siteBatchService = new SiteBatchService();
export default siteBatchService;
